// Archivo: src/services/fetchInterceptor.js

// Guardar la función fetch original
const originalFetch = window.fetch;

// Variables de control para manejar errores de autenticación
let authErrorCount = 0;
let lastAuthError = 0;
const MAX_AUTH_ERRORS = 2; // Reducido para testing
const AUTH_ERROR_WINDOW = 15000; // 15 segundos

// Variable para debugging
let debugMode = false;
const debugLog = (message, data = null) => {
  if (debugMode) {
    console.log(`[FETCH_INTERCEPTOR] ${message}`, data || '');
  }
};

// Leer base API desde variable de entorno. No usar fallback con IP real.
// Si no está definida, asumimos rutas relativas (/api/*) en las llamadas.
const API_BASE = process.env.REACT_APP_API_URL || '';

// Helper para comprobar si una URL corresponde a nuestra API.
// Soporta URLs absolutas y rutas relativas que comiencen con /api
const isApiCall = (url) => {
  try {
    if (typeof url !== 'string') return false;

    const base = API_BASE.replace(/\/$/, '');
    if (url.includes(base)) return true;
    if (url.startsWith('/api') || url.startsWith('/api/')) return true;
    return false;
  } catch (e) {
    return false;
  }
};

// Función para obtener el token actual
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  debugLog('Getting token:', token ? `${token.substring(0, 10)}...` : 'No token');
  return token;
};

// Función para verificar si el usuario está en página de login
const isOnLoginPage = () => {
  const pathname = window.location.pathname;
  // Solo considerar páginas de login/auth si son EXACTAMENTE esas rutas
  const loginPaths = ['/login', '/auth', '/login/', '/auth/'];
  const result = loginPaths.includes(pathname) || 
                 pathname.startsWith('/login?') ||
                 pathname.startsWith('/auth?');
  debugLog('Is on login page:', { pathname, result, loginPaths });
  return result;
};

// Función para limpiar sesión con validación adicional
const clearSessionOnAuthError = (isDefinitiveError = false, reason = '') => {
  const now = Date.now();
  
  debugLog('clearSessionOnAuthError called', { 
    isDefinitiveError, 
    reason, 
    authErrorCount, 
    isOnLoginPage: isOnLoginPage() 
  });
  
  // Si estamos en login, no hacer nada
  if (isOnLoginPage()) {
    debugLog('On login page, skipping session clear');
    return;
  }

  // Si es un error definitivo, limpiar inmediatamente
  if (isDefinitiveError) {
    debugLog('Definitive error, clearing session immediately');
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    authErrorCount = 0;
    window.dispatchEvent(new CustomEvent('sessionExpired'));
    return;
  }

  // Para errores no definitivos, usar sistema de conteo
  // Resetear contador si ha pasado mucho tiempo desde el último error
  if (now - lastAuthError > AUTH_ERROR_WINDOW) {
    authErrorCount = 0;
    debugLog('Resetting error count due to time window');
  }
  
  authErrorCount++;
  lastAuthError = now;
  
  debugLog(`Auth error ${authErrorCount}/${MAX_AUTH_ERRORS}`);
  
  // Solo limpiar sesión si hemos tenido muchos errores consecutivos
  if (authErrorCount < MAX_AUTH_ERRORS) {
    debugLog(`Error ${authErrorCount}/${MAX_AUTH_ERRORS}, NOT clearing session yet`);
    return;
  }

  debugLog('Max errors reached, clearing session');
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  authErrorCount = 0;
  window.dispatchEvent(new CustomEvent('sessionExpired'));
};

// Función para verificar si es un error definitivo de autenticación
const isDefinitiveAuthError = (response, errorData) => {
  const definitiveMessages = [
    'token inválido',
    'token expirado', 
    'invalid token',
    'expired token',
    'unauthorized access',
    'token no válido'
  ];

  let isDefinitive = false;
  if (errorData && errorData.message) {
    const message = errorData.message.toLowerCase();
    isDefinitive = definitiveMessages.some(msg => message.includes(msg));
  }

  debugLog('Checking if definitive auth error:', { 
    status: response.status, 
    errorMessage: errorData?.message, 
    isDefinitive 
  });

  return isDefinitive;
};

// Función para reintentar petición con delay
const retryWithDelay = async (url, options, delay = 2000) => {
  debugLog(`Retrying request after ${delay}ms delay`);
  await new Promise(resolve => setTimeout(resolve, delay));
  return originalFetch(url, options);
};

// Interceptor personalizado mejorado
window.fetch = async (...args) => {
  let [url, options = {}] = args;
  
  debugLog('Fetch intercepted:', { url, method: options.method || 'GET' });

  // Solo interceptar llamadas a nuestra API
  if (isApiCall(typeof url === 'string' ? url : '')) {
    debugLog('API call detected, processing...');
    
    // Asegurar que options.headers existe
    options.headers = options.headers || {};
    
    // Obtener token actual
    const token = getAuthToken();
    
    // Si hay token y no se está enviando ya Authorization, agregarlo
    if (token && !options.headers['Authorization'] && !options.headers.authorization) {
      options.headers['Authorization'] = `Bearer ${token}`;
      debugLog('Added Authorization header');
    } else if (!token) {
      debugLog('WARNING: No token available for API call');
    }

    // Asegurar headers básicos si no existen
    if (!options.headers['Accept']) {
      options.headers['Accept'] = 'application/json';
    }

    // Para peticiones que no son FormData, agregar Content-Type
    if (options.body && !(options.body instanceof FormData) && !options.headers['Content-Type']) {
      options.headers['Content-Type'] = 'application/json';
    }
    
    debugLog('Final headers:', options.headers);
  }

  try {
    // Realizar la petición
    let response = await originalFetch(url, options);
    
    debugLog('Response received:', { 
      status: response.status, 
      ok: response.ok,
      url: url 
    });

  // Definir rutas que deben ignorar el manejo de errores de sesión 401/403 usando Expresiones Regulares
  const excludedPaths = [
    /\/login$/,
    /\/forgotPassword$/,
    /\/resetPassword$/,
    /^\/api\/services\/?$/, // Coincide con /api/services y /api/services/
    /^\/api\/services\/\w+$/, // Coincide con /api/services/{id}
    /^\/api\/services\/\w+\/sections$/, // Coincide con /api/services/{id}/sections
    /^\/api\/sections\/\w+$/, // Coincide con /api/sections/{id}
    /^\/api\/sections\/\w+\/subsections$/, // Coincide con /api/sections/{id}/subsections
    /^\/api\/subsections\/\w+$/, // Coincide con /api/subsections/{id}
  ];

  // Extraer el pathname de la URL para una comparación más precisa
  const currentPath = typeof url === 'string' ? new URL(url, window.location.origin).pathname : '';
  const isExcludedPath = excludedPaths.some(regex => regex.test(currentPath));

  // Interceptar errores de autenticación en llamadas a nuestra API, EXCEPTO en rutas excluidas
  if ((response.status === 401 || response.status === 403) && isApiCall(typeof url === 'string' ? url : '') && !isExcludedPath) {
      
      debugLog('Auth error detected, analyzing...');
      
      let errorData = {};
      try {
        const responseClone = response.clone();
        errorData = await responseClone.json();
        debugLog('Error data from server:', errorData);
      } catch (e) {
        debugLog('Could not parse error response');
      }

      // Verificar si es un error definitivo
      const isDefinitive = isDefinitiveAuthError(response, errorData);
      
      // Para errores no definitivos, intentar reintentar una vez
      if (!isDefinitive && !options._isRetry && authErrorCount < MAX_AUTH_ERRORS) {
        debugLog('Attempting retry for non-definitive error...');
        
        const retryOptions = { ...options, _isRetry: true };
        
        try {
          response = await retryWithDelay(url, retryOptions, 2000);
          debugLog('Retry response:', { status: response.status, ok: response.ok });
          
          // Si el reintento es exitoso, resetear contador de errores
          if (response.ok) {
            authErrorCount = 0;
            debugLog('Retry successful, reset error count');
            return response;
          }
        } catch (retryError) {
          debugLog('Retry failed:', retryError.message);
        }
      }
      
      // Disparamos el evento para que la UI reaccione
      window.dispatchEvent(new CustomEvent('sessionExpired'));

      // Añadimos un log claro para el desarrollador
      console.log('[FETCH_INTERCEPTOR] Sesión expirada. Redirección en curso.');

      // Devolvemos una respuesta simulada para que la cadena de promesas no falle
      // y el navegador no lo marque como un error rojo.
      return new Response(JSON.stringify({ message: 'Sesión expirada' }), {
        status: 200, // Simulamos un OK
        headers: { 'Content-Type': 'application/json' },
      });
    }

  // Si la respuesta es exitosa, resetear contador de errores de auth
  if (response.ok && isApiCall(typeof url === 'string' ? url : '')) {
      if (authErrorCount > 0) {
        debugLog('Successful API call, resetting error count');
        authErrorCount = 0;
      }
    }

    return response;
  } catch (error) {
    debugLog('Fetch error caught:', { 
      name: error.name, 
      message: error.message,
      status: error.status 
    });
    
    // Manejar errores de red de una manera más amigable
    if (isApiCall(typeof url === 'string' ? url : '')) {
      if (error.name === 'TypeError' || error.message.includes('Failed to fetch')) {
        console.log(`[FETCH_INTERCEPTOR] No se pudo conectar con el servidor. Verifique la conexión de red.`);
      } else {
        // Para otros errores, simplemente los registramos sin que sea un error rojo
        console.log(`[FETCH_INTERCEPTOR] Ocurrió un error durante la petición: ${error.message}`);
      }
    }
    
    // Devolvemos una promesa que nunca se resuelve para detener la cadena
    return new Promise(() => {});
  }
};

// Funciones de utilidad para debugging
window.interceptorDebug = {
  enable: () => { debugMode = true; console.log('Debug mode enabled'); },
  disable: () => { debugMode = false; console.log('Debug mode disabled'); },
  status: () => ({
    authErrorCount,
    lastAuthError: lastAuthError ? new Date(lastAuthError).toISOString() : 'Never',
    maxErrors: MAX_AUTH_ERRORS,
    isOnLoginPage: isOnLoginPage(),
  hasToken: !!getAuthToken(),
  apiBase: API_BASE,
    debugMode
  }),
  reset: () => { 
    authErrorCount = 0; 
    lastAuthError = 0; 
    debugLog('Error count reset'); 
  }
};

export { originalFetch };